import { defineStore } from 'pinia';
import { cloneDeep, get, each, find, map, has, findIndex } from 'lodash';
import Vue from 'vue';
import uniqid from 'uniqid';

import ShippingMethodApi from '@/extensions/shop/store/api/shippingMethod';
import MemberTagApi from '@/extensions/restrictedArea/store/api/memberTag';
import CustomerGroupApi from '@/extensions/shop/store/api/customerGroup';
import TaxApi from '@/extensions/shop/store/api/tax';

import pinia from '@/pinia';
import { useSetupStore } from '@/stores';
import { set } from '@/helpers';

const newPrice = {
    id: uniqid('new:'),
    type: 'main',
    value: '',
    start_at: null,
    end_at: null,
    min_qty: null,
    tax_rate: 'regular',
    tax: null,
    customerGroup: null,
};

const defaultForm = {
    name: '',
    description: '',
    additional_info: '',
    service: '',
    mainPrice: {
        value: 0
    },
    prices: [newPrice],
    shippingMethodRules: [],
    tracking_link: '',
    active: true
};

const shippingMethodPriceRules = {
    prices: []
};

const newRuleObject = {
    name: '',
    type: 'price',
    min_points: undefined,
    max_points: undefined,
    min_sum: undefined,
    max_sum: undefined,
    memberTags: [],
    customerGroups: [],
    countries: []
};

const shippingMethodRule = {
    name: 'required',
    type: 'required',
    min_points: 'required',
    max_points: '',
    min_sum: 'required',
    max_sum: '',
    countries: 'required',
    memberTags: 'required',
};

export const useShopShippingStore = defineStore('shopShipping', {
    state: () => ({
        loading: true,
        itemsPaginated: {
            path: null,
            variables: {
                paginationSizes: [25, 50, 100]
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            link: {},
            items: []
        },
        rules: {
            name: 'required',
            service: 'required',
            mainPrice: { value: 'required' },
            shippingMethodRules: []
        },
        options: {
            customerGroup: {
                loading: false,
                items: [],
            },
            memberTag: {
                loading: false,
                items: []
            },
            tax: {
                loading: false,
                items: []
            },
            priceType: {
                loading: false,
                items: [
                    { value: 'main', label: 'Standard' },
                    { value: 'group', label: 'Kundengruppenpreis' },
                ]
            },
        },
        form: cloneDeep(defaultForm),
        shippingMethodPriceRules: cloneDeep(shippingMethodPriceRules)
    }),

    getters: {},

    actions: {
        addMissingMainPrice() {
            Vue.set(this.form.prices, this.form.prices.length, cloneDeep(newPrice));
        },

        insertTaxOptions(taxes) {
            Vue.set(this.options.tax, 'items', taxes);
        },

        resetForm() {
            Vue.set(this, 'form', cloneDeep(defaultForm));
        },

        addRule(name, type = 'price') {
            let newRule = cloneDeep(newRuleObject);
            newRule.id = uniqid('new:');
            newRule.name = name;
            newRule.type = type;

            let copy = cloneDeep(this.form.shippingMethodRules);
            copy.push(newRule);

            Vue.set(this.form, 'shippingMethodRules', copy);
            this.rules.shippingMethodRules.push(shippingMethodRule);
        },

        removeRule(index) {
            Vue.delete(this.form.shippingMethodRules, index);
            Vue.delete(this.rules.shippingMethodRules, index);
        },

        updateRuleValidation({ index, path, value }) {
            const ruleValue = get(this.form.shippingMethodRules, index);
            switch (path) {
                case 'min_sum':
                case 'max_sum':
                case 'min_points':
                case 'max_points':
                    let key = 'sum';
                    if (['min_points', 'max_points'].includes(path)) key = 'points';

                    if (ruleValue[`max_${key}`] == null) {
                        const min_rule = `required`;
                        Vue.set(this.rules.shippingMethodRules[index], `min_${key}`, min_rule);
                        const max_rule = undefined;
                        Vue.set(this.rules.shippingMethodRules[index], `max_${key}`, max_rule);
                    } else if (ruleValue[`min_${key}`] == null) {
                        const min_rule = `max_value:${ruleValue[`max_${key}`] || Infinity}`;
                        Vue.set(this.rules.shippingMethodRules[index], `min_${key}`, min_rule);
                        const max_rule = `min_value:${ruleValue[`min_${key}`] || 0}|required`;
                        Vue.set(this.rules.shippingMethodRules[index], `max_${key}`, max_rule);
                    } else {
                        const min_rule = `max_value:${ruleValue[`max_${key}`] || Infinity}|required`;
                        Vue.set(this.rules.shippingMethodRules[index], `min_${key}`, min_rule);
                        const max_rule = `min_value:${ruleValue[`min_${key}`] || 0}`;
                        Vue.set(this.rules.shippingMethodRules[index], `max_${key}`, max_rule);
                    }
                    break;
            }
        },

        resetRules() {
            Vue.set(this.form, 'shippingMethodRules', []);
        },

        insertMemberTagOptions(tags) {
            Vue.set(this.options.memberTag, 'items', tags);
        },

        insertCustomerGroupOptions(customerGroups) {
            Vue.set(this.options.customerGroup, 'items', customerGroups);
        },

        addItemsPaginatedToState(data) {
            this.itemsPaginated.items = data.items;
            this.itemsPaginated.pageInfo = cloneDeep(data.pageInfo);
            this.itemsPaginated.allColumns = cloneDeep(data.config.allColumns);
            this.itemsPaginated.link = cloneDeep(data.config.link);
            this.itemsPaginated.showFilters = get(data.config, 'showFilters') || false;
            this.itemsPaginated.defaultSort = get(data.config, 'defaultSort') || null;
        },

        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            each(this.itemsPaginated.allColumns, column => {
                let allowed = find(activeColumnKeys, value => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.itemsPaginated.activeColumns = activeColumns;
            }
        },

        updateForm({ path, value }) {
            set(this.form, path, value);
        },

        updatePrice(price) {
            if (price.includes(',')) price = price.replace(',', '.');
            Vue.set(this.form.mainPrice, 'value', price);
        },

        updateLoading(value) {
            this.loading = value;
        },

        defineFilterVisibility(value) {
            Vue.set(this.itemsPaginated, 'showFilters', value);
        },

        defineItemsPerPage(perPage) {
            this.itemsPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.itemsPaginated.pageInfo.currentPage = currentPage;
        },

        defineFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.itemsPaginated.filters, key);
            } else {
                Vue.set(this.itemsPaginated.filters, key, value);
            }
        },

        defineSorting({ key, value }) {
            Vue.set(this.itemsPaginated.variables, 'order', key);
            Vue.set(this.itemsPaginated.variables, 'direction', value);
        },

        insertShippingMethod(shippingMethod) {
            if (shippingMethod.mainPrice && shippingMethod.mainPrice.value == null) shippingMethod.mainPrice.value = 0;
            if (shippingMethod.shippingMethodRules) {
                for (let i = 0; i < shippingMethod.shippingMethodRules.length; i++) {
                    this.rules.shippingMethodRules.push(shippingMethodRule);
                }
            }
            Vue.set(this, 'form', shippingMethod);
        },

        addFormPrice(price) {
            const index = get(this, 'form.prices.length', 0);
            Vue.set(this.shippingMethodPriceRules.prices, index, {
                type: 'required',
                value: 'required|min_value:0',
                min_qty: 'required|integer|min_value:1',
                start_at: 'required',
                end_at: 'required',
                customerGroup: { id: 'required' }
            });
            if (!price.id) price.id = uniqid('new:');
            Vue.set(this.form.prices, index, price);
        },

        updateFormPrice({ price, field, value }) {
            const prices = get(this.form, 'prices') || [];
            const index = findIndex(prices, { id: price.id });

            switch (field) {
                case 'tax':
                case 'customerGroup':
                    if (typeof value !== 'object' || value === null) value = null;
                    break;
                case 'value':
                case 'net_value':
                case 'price':
                case 'min_qty':
                    if (isNaN(value)) value = null;
                    break;
            }

            if (index >= 0) {
                Vue.set(this.form.prices[index], field, value);
            }
        },

        deleteFormPrice({ index }) {
            Vue.delete(this.form.prices, index);
            Vue.delete(this.shippingMethodPriceRules.prices, index);
        },

        // Async actions
        createShippingMethod(shippingMethod) {
            return ShippingMethodApi.create(shippingMethod).then((shippingMethod) => {
                this.insertShippingMethod(cloneDeep(shippingMethod));
                return shippingMethod;
            });
        },

        updateShippingMethod(shippingMethod) {
            return ShippingMethodApi.update(shippingMethod).then((shippingMethod) => {
                this.insertShippingMethod(cloneDeep(shippingMethod));
                return shippingMethod;
            });
        },

        // Partial patch used by the list's inline switch - does not touch `form`, since the list
        // and the edit form are independent and the list may be toggling a row that isn't loaded
        // into the form at all.
        updateShippingMethodActive({ id, active }) {
            return ShippingMethodApi.update({ id, active });
        },

        deleteShippingMethod(id) {
            return ShippingMethodApi.delete(parseInt(id));
        },

        fetchPaginatedItems(shopID, cmsID) {
            return ShippingMethodApi.fetchShippingMethodsPaginated(this.itemsPaginated, shopID, cmsID).then(data => {
                this.addItemsPaginatedToState(data);

                const activeColumnKeys = map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        fetchShippingMethod(id) {
            return ShippingMethodApi.fetchShippingMethod(id).then(data => {
                this.insertShippingMethod(cloneDeep(data));
            });
        },

        fetchTagOptions(cmsID) {
            return MemberTagApi.fetchList(cmsID)
                .then(tags => {
                    this.insertMemberTagOptions(tags);
                })
                .catch(err => {
                    throw err;
                });
        },

        fetchTaxOptions(shopID) {
            const setupStore = useSetupStore(pinia);
            if (has(setupStore, 'activeShop.defaultTax'))
                this.insertTaxOptions([setupStore.activeShop.defaultTax]);

            return TaxApi.fetchList(shopID)
                .then(taxes => {
                    this.insertTaxOptions(taxes);
                })
                .catch(err => {
                    throw err;
                });
        },

        fetchCustomerGroupOptions(cmsID) {
            return CustomerGroupApi.fetchList(cmsID)
                .then((customerGroups) => {
                    this.insertCustomerGroupOptions(customerGroups);
                })
                .catch((err) => {
                    throw err;
                });
        },
    }
});
